import * as React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import style from "./news.scss";
import NewsDetail from "./NewsDetail";
import ErrorMessage from "components/error-message/ErrorMessage";
import EmptyStateIcon from "components/icons/EmptyState";
import { LoadingIndicator } from "components/loading-indicator/LoadingIndicator";
import { NewsPage, newsService } from "services/news/NewsService";
import { Action, Category, usageStatisticsService } from "services/statistics/UsageStatisticsService";
import { StoreState } from "store";
import buttons from "styles/buttons.scss";
import { createDateLabel, getInitials } from "utils/format";
import { logger } from "utils/logging";

const LIMIT = 10;
const WORDS_PER_MINUTE = 200;

const extractExcerpt = (htmlContent: string, maxLength = 200): string => {
    const text = htmlContent.replace(/<[^>]*>/g, "").trim();
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
};

const calculateReadTime = (content: string): number => {
    const words = content.trim().split(/\s+/).length;
    const minutes = Math.ceil(words / WORDS_PER_MINUTE);
    return Math.max(1, minutes);
};

const News = (): JSX.Element => {
    const { t } = useTranslation();
    const language = useSelector((state: StoreState) => state.languageReducer.lang);
    const [pages, setPages] = React.useState<NewsPage[]>([]);
    const [displayedCount, setDisplayedCount] = React.useState<number>(LIMIT);
    const [error, setError] = React.useState<string | null>(null);
    const [nextCursor, setNextCursor] = React.useState<string | undefined>(undefined);
    const [selectedNews, setSelectedNews] = React.useState<NewsPage | null>(null);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    const fetchNewsPages = React.useCallback(async (cursor?: string, append = false) => {
        try {
            setError(null);
            if (!append) {
                setIsLoading(true);
            }
            const response = await newsService.fetchNews(cursor, 100);
            const sortedPages = response.pages.sort((a, b) => {
                const dateA = a.history?.createdDate || a.history?.lastUpdated?.when || "";
                const dateB = b.history?.createdDate || b.history?.lastUpdated?.when || "";
                return new Date(dateB).getTime() - new Date(dateA).getTime();
            });
            setPages((prev) => {
                if (append) {
                    const existingIds = new Set(prev.map((p) => p.id));
                    const newPages = sortedPages.filter((p) => !existingIds.has(p.id));
                    return [...prev, ...newPages];
                }
                return sortedPages;
            });
            setNextCursor(response.pagination.next_cursor);
            if (append) {
                setDisplayedCount((prev) => prev + LIMIT);
            }
        } catch (err) {
            logger.error(err);
            setError(err instanceof Error ? err.message : "Failed to fetch news");
        } finally {
            setIsLoading(false);
        }
    }, []);

    React.useEffect(() => {
        fetchNewsPages();
    }, [fetchNewsPages, language]);

    if (selectedNews) {
        return (
            <NewsDetail
                newsItem={selectedNews}
                onBack={() => setSelectedNews(null)}
                authorName={selectedNews.authorName}
                dateLabel={selectedNews.dateLabel}
                readTime={selectedNews.readTime}
                title={selectedNews.title}
            />
        );
    }

    if (error) {
        return <ErrorMessage />;
    }

    return (
        <div className={style.newsContainer}>
            {isLoading ? (
                <div className={style.centeredState}>
                    <LoadingIndicator />
                </div>
            ) : pages.length === 0 ? (
                <div className={style.centeredState}>
                    <EmptyStateIcon />
                    <p>{t("Routes.news.emptyStateMessage")}</p>
                </div>
            ) : (
                <>
                    <div className={style.newsList}>
                        {pages.slice(0, displayedCount).map((page) => {
                            const pageId = page.id;
                            const title = page.title;
                            const authorName =
                                page.history?.createdBy?.displayName ||
                                page.history?.lastUpdated?.by?.displayName ||
                                "Unknown";
                            const excerpt = page.excerpt ? extractExcerpt(page.excerpt) : "";
                            const dateLabel = createDateLabel(
                                page.history?.createdDate || page.history?.lastUpdated?.when || ""
                            );
                            const readTime = calculateReadTime(page.excerpt || title);

                            return (
                                <div key={pageId} className={style.newsItem}>
                                    <h2>
                                        <a
                                            href="#"
                                            onClick={async (e) => {
                                                e.preventDefault();
                                                try {
                                                    const { page: fullPage } = await newsService.fetchNewsById(pageId);
                                                    setSelectedNews({
                                                        ...fullPage,
                                                        authorName,
                                                        dateLabel,
                                                        readTime,
                                                        title,
                                                    });
                                                    usageStatisticsService.sendEvent({
                                                        category: Category.HEADER,
                                                        action: Action.VIEW_NEWS_ARTICLE,
                                                        label: title,
                                                    });
                                                } catch (err) {
                                                    logger.error(err);
                                                    setSelectedNews({
                                                        ...page,
                                                        authorName,
                                                        dateLabel,
                                                        readTime,
                                                        title,
                                                    });
                                                }
                                            }}
                                        >
                                            {title}
                                        </a>
                                    </h2>
                                    {excerpt && <p className={style.newsExcerpt}>{excerpt}</p>}
                                    <p className={style.newsMeta}>
                                        {t("Routes.news.by")}
                                        <span className={style.authorInfo}>
                                            <span className={style.authorInitial}>{getInitials(authorName)}</span>
                                            <span className={style.authorName}>{authorName}</span>
                                        </span>
                                        • {dateLabel} • {readTime} {t("Routes.news.readTime")}
                                    </p>
                                </div>
                            );
                        })}
                    </div>
                    {(displayedCount < pages.length || nextCursor) && (
                        <button
                            className={buttons.loadMoreButtonWithoutIcon}
                            onClick={() => {
                                if (displayedCount < pages.length) {
                                    setDisplayedCount(displayedCount + LIMIT);
                                } else if (nextCursor) {
                                    fetchNewsPages(nextCursor, true);
                                }
                            }}
                        >
                            {t("Common.loadMore")}
                        </button>
                    )}
                </>
            )}
        </div>
    );
};

export default News;
